import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ImageUploadModal from '../../../../Components/ModalPopup/ImageUploadModal';
import type { ImageUploadData } from '../../../../Components/ModalPopup/ImageUploadModal';
import { getImagesByProductContext, IMAGE_PICKER_EXTENSIONS } from '../../../AssetLibrary/Assets/store/asset.service';
import { showBigImage, hideImage } from '../../../../Components/helper';
import { DeleteIcon, NoimageIcon, UploadIcon } from '../../../../bombiix-icon';
import type { BombiixCellRendererParams, BombiixRowId } from '../../../../Components/BombiixAGGrid';
import { TableRow } from './types';

type ImageCellProps = BombiixCellRendererParams<TableRow, string> & {
  isInlineRow?: (data: TableRow) => boolean;
};

/** Delete and Upload glyph size in the editable cell (Figma node 15632:38117). */
const ACTION_ICON_SIZE = 16;

/**
 * Image cell for the table view. Read-only rows show a hover-preview thumbnail.
 *
 * An editable row renders the "Image Input" cell from Figma (node 15632:38116): a 32px thumbnail
 * that opens the shared ImageUploadModal on click, and — on hover or keyboard focus — a stacked
 * Delete / Upload pair beside it. The actions take no layout space until they are shown, so the
 * default state centres the thumbnail exactly as the Default variant does.
 *
 * The modal mirrors the Create Product page: a saved product searches its own assets (by
 * productId), a draft row with no id yet searches by its brand / licensing context. Delete only
 * flags the row; the removal is sent with the save like any other edit, so Cancel restores the
 * picture and nothing is destroyed until the user commits.
 * @param params - AG Grid cell params plus the inline-row predicate.
 * @returns the image cell.
 */
function ImageCell(params: ImageCellProps) {
  const { data, value, isInlineRow, onRowChange, getRowId } = params;
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const editable = data && isInlineRow ? isInlineRow(data) : false;
  const src = (data?.thumbNailImg as string) || (data?.image as string) || value || '';
  const previewSrc = (data?.image as string) || src;
  const rowId: BombiixRowId | undefined = getRowId && data ? getRowId(data) : data?._id;
  const isDraft = Boolean(data?.__isDraft);
  const brandId = toRefId(data?.brandId);
  const subBrandId = toRefId(data?.subBrandId);
  const licensorId = toRefId(data?.licensorId);
  const licenseId = toRefId(data?.licenseId);
  const likenessId = toRefId(data?.likenessId);

  /**
   * Page fetcher for a draft row, which owns no assets yet: scopes the library search to the row's
   * brand / sub-brand / licensing selections. Mirrors CreateProduct's `fetchProductImages`.
   * @param search - free-text search.
   * @param page - 1-based page to load.
   * @param limit - page size.
   * @param allowedImageExtensions - extension filter forwarded by the modal.
   * @returns one page of context-filtered library images.
   */
  const fetchDraftImages = useCallback(
    (search: string, page: number, limit: number, allowedImageExtensions?: string) =>
      getImagesByProductContext({
        search,
        page,
        limit,
        ...(allowedImageExtensions ? { allowedImageExtensions } : {}),
        ...(brandId ? { brandIds: [brandId] } : {}),
        subBrandId: subBrandId || null,
        ...(licensorId ? { licensorIds: [licensorId] } : {}),
        ...(licenseId ? { licenseIds: [licenseId] } : {}),
        ...(likenessId ? { likenessIds: [likenessId] } : {}),
      }),
    [brandId, subBrandId, licensorId, licenseId, likenessId],
  );

  /*
   * A presigned URL that fails to load — expired, or a raw S3 key the server emitted when
   * presigning returned nothing — painted the browser's broken-image glyph in every row. Fall back
   * to the same placeholder an imageless product already shows, as the card list and the form do.
   */
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [src]);
  const showImage = Boolean(src) && !imageFailed;

  /**
   * Write the chosen image onto the row. A library asset travels as its S3 key with `assetId`, so
   * the backend copies the existing asset instead of re-uploading; a dropped file travels as a
   * base64 data URL. `thumbNailImg` carries a displayable URL so the cell shows the new pick.
   * @param image - the image value to save.
   * @param displayUrl - URL the thumbnail renders from.
   * @param assetId - library asset id, or null for a dropped file.
   * @param addToDesign - the modal's "Add to Design/Details" tick.
   * @param addToAssetLibrary - the modal's "Add to Asset Library" tick; only a dropped file can carry it.
   */
  const applyImage = (image: string, displayUrl: string, assetId: string | null, addToDesign: boolean, addToAssetLibrary: boolean) => {
    if (rowId === undefined) return;
    onRowChange?.(rowId, 'image', image);
    onRowChange?.(rowId, 'fullImg', image);
    onRowChange?.(rowId, 'thumbNailImg', displayUrl);
    onRowChange?.(rowId, 'isUploadAsset', Boolean(assetId));
    onRowChange?.(rowId, 'assetId', assetId);
    onRowChange?.(rowId, 'isIncludeDesign', addToDesign);
    onRowChange?.(rowId, 'isAddToAssetLibrary', addToAssetLibrary);
    onRowChange?.(rowId, '__imageRemoved', false);
  };

  /**
   * Commit the upload modal's selection. Mirrors CreateProduct's `handleNewImageUploadSave`.
   * @param upload - the modal's selection.
   */
  const handleUploadSave = ({ file, assetUrl, assetKey, assetId, addToDesign, addToAssetLibrary }: ImageUploadData) => {
    const isAddToDesign = Boolean(addToDesign);
    const isAddToAssetLibrary = Boolean(addToAssetLibrary);
    if (assetKey) {
      applyImage(assetKey, assetUrl || '', assetId || null, isAddToDesign, false);
      return;
    }
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const imageRes = typeof reader.result === 'string' ? reader.result : '';
      if (imageRes) applyImage(imageRes, imageRes, null, isAddToDesign, isAddToAssetLibrary);
    };
    reader.readAsDataURL(file);
  };

  const handleDelete = () => {
    if (rowId === undefined) return;
    onRowChange?.(rowId, 'image', '');
    onRowChange?.(rowId, 'fullImg', '');
    onRowChange?.(rowId, 'thumbNailImg', '');
    onRowChange?.(rowId, 'isUploadAsset', false);
    onRowChange?.(rowId, 'assetId', null);
    onRowChange?.(rowId, 'isAddToAssetLibrary', false);
    onRowChange?.(rowId, '__imageRemoved', true);
  };

  const thumb = (
    <figure className="ptv-image-cell__figure" onMouseOver={(event) => showBigImage(event, 'ptv-image-cell__preview')} onMouseLeave={hideImage}>
      {showImage ? (
        <img
          alt={(data?.number as string) || t('COMMON_LABEL.PRODUCT_IMAGE')}
          src={src}
          data-full-src={previewSrc}
          className="ptv-image-cell__img"
          loading="lazy"
          decoding="async"
          width={32}
          height={32}
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="ptv-image-cell__placeholder">
          <NoimageIcon size={20} />
        </span>
      )}
    </figure>
  );

  if (!editable) {
    return <span className="ptv-image-cell">{thumb}</span>;
  }

  return (
    <span className="ptv-image-cell ptv-image-cell--editable">
      <button
        type="button"
        className="ptv-image-cell__trigger"
        onClick={() => setOpen(true)}
        aria-label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.UPLOAD')}
      >
        {thumb}
      </button>
      <span className="ptv-image-cell__actions">
        <button
          type="button"
          className="ptv-image-cell__action"
          onClick={handleDelete}
          disabled={!showImage}
          aria-label={t('CUSTOM_MODULE.DELETE_IMAGE')}
        >
          <DeleteIcon size={ACTION_ICON_SIZE} />
        </button>
        <button
          type="button"
          className="ptv-image-cell__action"
          onClick={() => setOpen(true)}
          aria-label={t('CUSTOM_MODULE.UPLOAD_IMAGE')}
        >
          <UploadIcon size={ACTION_ICON_SIZE} />
        </button>
      </span>
      {open && (
        <ImageUploadModal
          show={open}
          productId={isDraft ? null : data?._id || null}
          {...(isDraft ? { fetchImages: fetchDraftImages } : {})}
          onClose={() => setOpen(false)}
          onSave={handleUploadSave}
          allowedImageExtensions={IMAGE_PICKER_EXTENSIONS}
          showAddToDesign
          showAddToAssetLibrary
          existingImage={previewSrc || null}
        />
      )}
    </span>
  );
}

export default ImageCell;

/**
 * Reduce a populated reference (or a bare id) on the row to its id string.
 * @param value - `{ _id }` object, id string, or empty.
 * @returns the id, or an empty string.
 */
function toRefId(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  const id = (value as { _id?: unknown })._id;
  return id ? String(id) : '';
}
